import { useMemo } from "react";
import { useOptionalAccess, type AccessModuleState } from "../lib/access.js";
import {
  BASE_MODULE,
  moduleForNavItem,
  type BillingModule,
  type ModuleBoundNavItem,
  type ModuleState,
} from "./moduleMap.js";

/**
 * A-MODUL-NAV1 (PASS 001699) — EIN zentraler Hook fuer den Modulzustand.
 * A-W4-WEB1 (W4 Schritt 7) — die Quelle ist die Zugriffsantwort.
 *
 * Hier stand eine zweite Abfrage: `GET /v1/me/modules` mit eigenem Cache,
 * eigenem Schluessel und eigenem `enforced`-Kurzschluss. Seit W4 traegt
 * `GET /v1/me/access` die Modulsicht des Akteurs mit (`modules`, §7.2), also
 * liest diese Datei sie von dort (`lib/access.ts`) und fragt nichts mehr selbst.
 * Damit gibt es im Client genau einen Lesepfad fuer Zugriff UND Module.
 *
 * Geblieben ist die Ableitung: die Navigation stellt genau drei Fragen
 * (ausblenden, kennzeichnen, normal zeigen), und die Richtung ist weiter
 * bewusst FAIL-OPEN:
 *
 *   - keine Antwort (Ladefehler, kein Provider) -> alle Module aktiv,
 *   - ein Modul, das die Antwort nicht nennt -> aktiv,
 *   - Basis immer aktiv.
 *
 * Begruendung unveraendert: eine leergefilterte, bezahlte Navigation nach einem
 * kurzen Netzfehler ist schlimmer als ein Menuepunkt, der zu einer
 * Server-Absage fuehrt. Ueber Schreibrechte entscheidet ohnehin der Server
 * (Lane C), der Client blendet nur ein und aus.
 *
 * Zwei Dinge sind dabei ehrlich zu benennen:
 *
 *   - `enforced` gibt es nicht mehr. Der Schalter war nie eine Client-Frage:
 *     ist die Durchsetzung aus, meldet der Server ohnehin jedes Modul `active`
 *     (`services/api/src/modules/billing-modules/me-modules.service.ts`), und
 *     `GET /v1/me/access` fuehrt das Feld gar nicht. Die Zustaende allein
 *     genuegen also; kein Produktionspfad hat `enforced` gelesen.
 *   - Faellt das Lesen des Modulbestands auf dem SERVER aus, antwortet
 *     `me-access.service.ts` bewusst fail closed (alles `hidden`) — anders als
 *     dieser Client. Die Entscheidung liegt damit beim Server, wo sie hingehoert;
 *     der Client faellt nur noch bei gar keiner Antwort auf.
 */

/**
 * Lesbare Sicht auf den Modulzustand. Die Navigation stellt genau drei Fragen:
 * ausblenden, kennzeichnen, oder normal zeigen.
 */
export interface ModuleView {
  /** Laeuft die erste Abfrage noch? Der Waechter wartet darauf, das Menue nicht. */
  readonly loading: boolean;
  readonly stateOf: (module: BillingModule) => ModuleState;
  readonly isHidden: (module: BillingModule) => boolean;
  readonly isReadOnly: (module: BillingModule) => boolean;
  readonly isNavItemHidden: (item: ModuleBoundNavItem) => boolean;
  readonly isNavItemReadOnly: (item: ModuleBoundNavItem) => boolean;
}

function viewFromStates(
  states: ReadonlyMap<BillingModule, ModuleState>,
  loading: boolean,
): ModuleView {
  const stateOf = (module: BillingModule): ModuleState => {
    // Basis wird nicht verkauft und bleibt immer aktiv (Owner 05.10.) — auch
    // wenn eine Antwort sie anders meldet.
    if (module === BASE_MODULE) return "active";
    return states.get(module) ?? "active";
  };
  const isHidden = (module: BillingModule): boolean => stateOf(module) === "hidden";
  const isReadOnly = (module: BillingModule): boolean => stateOf(module) === "read_only";
  return {
    loading,
    stateOf,
    isHidden,
    isReadOnly,
    isNavItemHidden: (item) => isHidden(moduleForNavItem(item)),
    isNavItemReadOnly: (item) => isReadOnly(moduleForNavItem(item)),
  };
}

/** Alles sichtbar — Vorbelegung fuer Komponenten ohne Shell-Kontext. */
export const ALL_MODULES_ACTIVE: ModuleView = viewFromStates(new Map(), false);

/** Reine Ableitung aus den Modulzustaenden der Zugriffsantwort — ohne React. */
export function moduleViewFrom(
  modules: readonly AccessModuleState[] | undefined,
  loading: boolean,
): ModuleView {
  const states = new Map<BillingModule, ModuleState>();
  for (const entry of modules ?? []) states.set(entry.module, entry.state);
  return viewFromStates(states, loading);
}

/**
 * Der zentrale Hook. Genau EIN Aufruf in der App (`AppShell`); alle anderen
 * Stellen bekommen die `ModuleView` als Eigenschaft durchgereicht, damit nicht
 * jede Nav-Komponente eine eigene Abfrage aufmacht. Der Routenwaechter ruft ihn
 * ebenfalls — er teilt sich jetzt den Cache-Eintrag der Zugriffsantwort.
 *
 * `useOptionalAccess`, nicht `useAccess`: der Waechter umhuellt jede Route, auch
 * die, die ausserhalb einer Session gerendert werden koennen. Ohne Session gibt
 * es keine Modulaussage — und ohne Aussage bleibt alles sichtbar (fail open).
 */
export function useModules(): ModuleView {
  const { modules, loading } = useOptionalAccess();
  return useMemo(() => moduleViewFrom(modules, loading), [modules, loading]);
}
